import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, ShieldCheck, ArrowLeft, KeyRound } from "lucide-react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SwanLogo, TaglineDivider } from "@/components/Brand";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("login"); // login | twofa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        setPendingToken(data.pending_token);
        setStep("twofa");
      } else {
        login(data.token, data.user);
        navigate("/");
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-2fa", { pending_token: pendingToken, code });
      if (data.used_backup) toast.success("Signed in with a recovery code");
      login(data.token, data.user);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="swan-bg min-h-screen flex flex-col items-center justify-center px-5 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex flex-col items-center text-center mb-2">
          <SwanLogo size={86} />
          <h1 className="font-serif font-bold text-white text-4xl mt-5">JTS Swanson</h1>
        </div>
        <TaglineDivider />

        {step === "login" ? (
          <form onSubmit={submitLogin} className="mt-6" data-testid="login-form">
            <h2 className="font-serif font-bold text-3xl text-white">Welcome back</h2>
            <p className="text-slate-400 mt-1 mb-7">Sign in to your secure portal</p>

            <label className="block text-slate-300 mb-2 text-sm">Email</label>
            <input
              data-testid="login-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@business.co.uk"
              className="w-full card-surface rounded-xl px-4 py-3.5 text-white outline-none focus:border-[#C9A84A]/60 transition-colors mb-5"
            />

            <label className="block text-slate-300 mb-2 text-sm">Password</label>
            <div className="relative mb-6">
              <input
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full card-surface rounded-xl px-4 py-3.5 pr-12 text-white outline-none focus:border-[#C9A84A]/60 transition-colors"
              />
              <button type="button" onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                data-testid="toggle-password" aria-label="Toggle password">
                {showPw ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {error && <p className="text-red-300 text-sm mb-4" data-testid="login-error">{error}</p>}

            <button
              data-testid="login-submit"
              disabled={busy}
              className="w-full bg-[#C9A84A] hover:bg-[#E5C158] text-[#0A0F1F] font-bold rounded-xl py-3.5 transition-colors active:scale-[0.99] disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        ) : (
          <form onSubmit={submit2fa} className="mt-6" data-testid="twofa-form">
            <div className="w-12 h-12 rounded-xl bg-[#C9A84A] flex items-center justify-center mb-5">
              <ShieldCheck className="text-[#0A0F1F]" size={26} />
            </div>
            <h2 className="font-serif font-bold text-3xl text-white">Two-factor check</h2>
            <p className="text-slate-400 mt-1 mb-7">
              {recovery ? "Enter one of your backup recovery codes." : "Enter the 6-digit code from your authenticator app."}
            </p>

            <input
              data-testid="twofa-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode={recovery ? "text" : "numeric"}
              autoFocus
              placeholder={recovery ? "ABCD-1234" : "1 2 3 4 5 6"}
              className="w-full card-surface rounded-xl px-4 py-4 text-white text-center text-2xl tracking-[0.3em] outline-none focus:border-[#C9A84A]/60 transition-colors mb-6"
            />

            {error && <p className="text-red-300 text-sm mb-4" data-testid="twofa-error">{error}</p>}

            <button
              data-testid="twofa-submit"
              disabled={busy}
              className="w-full bg-[#C9A84A] hover:bg-[#E5C158] text-[#0A0F1F] font-bold rounded-xl py-3.5 transition-colors active:scale-[0.99] disabled:opacity-60"
            >
              {busy ? "Verifying…" : "Verify & sign in"}
            </button>

            <button type="button" onClick={() => { setRecovery((r) => !r); setCode(""); setError(""); }}
              className="w-full text-center gold-text text-sm mt-5 hover:underline" data-testid="toggle-recovery">
              {recovery ? "Use your authenticator app instead" : "Lost your phone? Use a recovery code"}
            </button>

            <button type="button" onClick={() => { setStep("login"); setError(""); setCode(""); }}
              className="w-full card-surface rounded-xl py-3.5 mt-4 text-[#E5C158] font-semibold flex items-center justify-center gap-2 hover:bg-[#18243F] transition-colors">
              <ArrowLeft size={18} /> Back
            </button>
          </form>
        )}

        <div className="flex items-center justify-center gap-2 text-slate-500 text-sm mt-8">
          <Lock size={14} /> Bank-grade encryption · UK GDPR compliant
        </div>
      </motion.div>
    </div>
  );
}
